import { Button } from '@aecom-ai-engineering/react-components';
import { type Project } from '@consigli/types';
import { Plus } from 'lucide-react';
import { type FC, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_MARK_FALLBACK } from '@/components/agent-marks';
import { type ServiceData } from '@/features/project-services/use-project-services';
import { useServiceNavigation } from '@/features/project-services/use-service-navigation';

interface AgentCardProps {
  service: ServiceData;
  projectId: Project['id'];
  /** Inline SVG source. Undefined falls back to the generic mark. */
  mark?: string;
}

/**
 * One agent in the Agents tab's grid. Figma 11233:49001.
 *
 * Applied carries a badge, available an Add button; everything else is shared.
 * `service.icon`, the raster the legacy card uses as a hero, is unused.
 */
export const AgentCard: FC<AgentCardProps> = ({ service, projectId, mark }) => {
  const { t } = useTranslation();
  const descriptionId = useId();
  // Exactly the fields service-card.tsx passes. While both cards exist they must
  // reach the same destination for the same service; reshaping this call is how
  // that quietly stops being true.
  const { navigateToService } = useServiceNavigation({
    active: service.active,
    externalToSolutionViewer: service.externalToSolutionViewer,
    externalUrl: service.externalUrl,
    id: service.serviceName,
    newServiceRoute: service.newServiceRoute,
    openInSameWindow: service.openInSameWindow,
    projectId,
    serviceId: service.serviceId,
    serviceRoute: service.serviceRoute,
  });

  return (
    // `isolate` so the gradient below can sit at a negative z-index: inside a
    // stacking context that paints it over the card's own background but under
    // every piece of content, which is where a background belongs. Without it
    // the negative index would drop it behind the white fill entirely.
    //
    // `group` is what the gradient's hover reads. The stretched control covers
    // the whole card, so pointing anywhere on it hovers this element.
    //
    // 120ms ease is the design system's own interaction timing, from Button.css
    // — the border used to snap.
    <article className="group relative isolate flex flex-col gap-6 rounded-2xl border border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-6 transition-colors duration-[120ms] ease-[ease] hover:border-[var(--ds-color-border-secondary)]">
      {/* The frame's `brand-gradient` on hover: brand-light at the top falling
          to nothing at the bottom. Its own element rather than a background on
          the card, because `background-image` does not interpolate — a gradient
          swapped in on hover would appear at once where this fades. The radius
          matches the card's, which nothing clips for it. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-b from-[var(--ds-color-bg-brand-light)] to-transparent opacity-0 transition-opacity duration-[120ms] ease-[ease] group-hover:opacity-100"
      />

      {/* SVG source strings, so dangerouslySetInnerHTML is the only way in — an
          <img src> would drop the .ds-agent-logo classes.
          data-ds-agent-logo-inline is load-bearing: it is the only rule in
          Logos.css supplying --ds-color-text-invert, and the glyphs are
          currentColor, so without it they render dark on the dark-green tile.
          No render test sees that; the marquee's contract test pins the rule.

          An applied agent used to carry a check beside this. It is gone, and
          the row it shared with the logo went with it — the logo is the row's
          only occupant now, so the flex that spaced the two apart has nothing
          left to space. What an applied card still says for itself is that it
          offers no Add button, and its accessible name names the state. */}
      <span
        aria-hidden
        data-ds-agent-logo-inline
        dangerouslySetInnerHTML={{ __html: mark ?? AGENT_MARK_FALLBACK }}
      />

      {/* `flex-1` takes the height the grid stretches the card to, and the
          button's `mt-auto` spends it above the button. A short description
          then leaves the Add button on the card's floor, level with its
          neighbours in the row. gap-4 stays as the minimum between them. */}
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-col gap-2">
          {/* Wraps rather than being cut. It was one line and ellipsised, which
              read as a defect on the several titles German runs past the card
              — recoverable from the control's aria-label, but only by someone
              who thought to look.

              `text-wrap` over merely dropping `truncate`, so the intent is
              stated: the default would wrap too, and the next person to reach
              for `truncate` here should see that one line was the thing
              removed. */}
          <h3 className="text-base leading-6 font-medium text-wrap text-[var(--ds-color-text-primary)]">
            {service.title}
          </h3>
          {/* Always a string: every serviceConfig entry has a tooltipKey and t()
              returns the key on a miss.

              Clamped at three lines and held at their height. That used to
              make every card in a section exactly as tall as every other,
              because the title above was pinned to one line as well. It no
              longer is, so a two-line title makes its card taller and the grid
              stretches the rest of the row to match — the row still ends level,
              but the logos in it no longer sit at the same height as the row
              beside it. Interim: it goes when the copy fits three lines, about
              67 English characters. */}
          <p
            className="line-clamp-3 min-h-[54px] text-[13px] leading-[18px] text-[var(--ds-color-raw-stone-600)]"
            id={descriptionId}
          >
            {service.tooltip}
          </p>
        </div>

        {!service.active && (
          // A sibling of the card's control, never nested in it — nested buttons
          // are invalid HTML, which is why the legacy card reached for
          // role="button". z-10 lifts it over the overlay, tabIndex -1 keeps one
          // tab stop per card, and sm is the frame's 32 height and 6px gap.
          <Button
            // Brand green on hover, where `outline`'s own hover is a near-white
            // grey — this is the card's primary action and now says so. The ring
            // takes the fill's colour too, since the border token reads as a
            // grey edge once the inside is green.
            //
            // Utilities rather than a CSS override: Tailwind's utilities layer
            // sorts after the design system's components layer, so these win on
            // layer order without having to out-specify the library's
            // `:hover:not([disabled]):not([aria-disabled])`.
            //
            // Button.css fades background, border and opacity but not colour or
            // box-shadow, so the text and the ring snapped while the fill faded.
            // The transition list adds both at the library's 120ms ease.
            //
            // The ring is a raw `box-shadow`, not `shadow-[...]`. That utility
            // writes Tailwind's five-shadow stack with the ring last, and a
            // list that does not match the library's single inset shadow entry
            // for entry cannot interpolate, so it swapped in at once. One inset
            // of the library's own width fades like the rest.
            className="group/add relative z-10 mt-auto w-full transition-[background-color,border-color,color,box-shadow,opacity] duration-[120ms] ease-[ease] hover:bg-[var(--ds-color-bg-brand)] hover:text-[var(--ds-color-text-invert)] hover:[box-shadow:inset_0_0_0_calc(var(--ds-space-0-5)/2)_var(--ds-color-bg-brand)] motion-reduce:transition-none"
            onClick={() => void navigateToService()}
            size="sm"
            tabIndex={-1}
            variant="outline"
          >
            {/* A quarter turn on hover. The plus is symmetric, so it lands on
                itself and reads as motion, not as a different icon. 200ms
                ease-out is the project card's image zoom. */}
            <Plus
              className="transition-transform duration-200 ease-out group-hover/add:rotate-90 motion-reduce:transition-none"
              size={16}
              strokeWidth={1.75}
            />
            {t('project-overview.add-to-project')}
          </Button>
        )}
      </div>

      {/*
       * The whole card is the control, as a real <button> stretched over the
       * article rather than a div with role="button" — focus, Enter and Space
       * come free. Not the repeat guard: navigateToService calls window.open for
       * external-URL cards and every key repeat is its own trusted gesture, so
       * the popup blocker does not absorb them. Space activates on keyup and
       * cannot repeat; Enter activates on keydown and does.
       *
       * Named by service and state, because the badge is aria-hidden and the add
       * button is out of the tab order — a title alone reads the same for both
       * variants while they lead to different places.
       *
       * `title` here rather than on the text, which this covers; raising the text
       * above it would punch a hole in the click target. Name first and on its
       * own line, since the card cuts both it and the description. The native
       * tooltip, not the design system's, which is nowrap and max-content and
       * would need a stylesheet per add-context-modal.css.
       *
       * It is a mouse affordance only. VoiceOver does not language-detect a
       * description derived from `title`: it speaks it in whatever voice is
       * current, which drops CJK entirely — a Chinese description read as just
       * its one Latin run — and gives German an English accent. Text nodes,
       * aria-label and aria-describedby all switch voice correctly, so the
       * description points at the visible <p> instead. aria-describedby wins
       * over `title` for the accessible description, so the tooltip above stays
       * for sighted users and costs the screen reader nothing.
       */}
      <button
        aria-describedby={descriptionId}
        aria-label={
          service.active
            ? t('project-overview.agent-applied', { name: service.title })
            : t('project-overview.agent-available', { name: service.title })
        }
        className="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)]"
        onClick={() => void navigateToService()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && event.repeat) event.preventDefault();
        }}
        title={`${service.title}\n${service.tooltip}`}
        type="button"
      />
    </article>
  );
};
